import { FormControl, InputLabel, ListItem, NativeSelect, TextField } from "@mui/material"
import React from "react"
import { NumericFormat } from "react-number-format"
import { CurveFitEntry, UltimateStrengthUnit } from "../../../../models/curvefit"
import { MaturityMethodEntry } from "../../../../models/maturitymethod"
import { floatOrNull } from "../TemperatureLimitsView"

interface Props {
  maturityMethod: MaturityMethodEntry
  entry: CurveFitEntry
  onEntryChange: (entry: CurveFitEntry) => void
  disabled?: boolean
  highlightRequiredFields?: boolean
}

const LogarithmicDataView = (props: Props) => {
  if (!props.maturityMethod.temperatureUnit) {
    return <></>
  }

  const strengthUnitDisplay = props.entry.parameters.strengthUnit === "mpa" ? "MPa" : "psi"

  return (
    <>
      <ListItem sx={{
        paddingLeft: "0px",
        paddingRight: "0px"
      }}>
        <FormControl fullWidth error={props.highlightRequiredFields && props.entry.parameters.strengthUnit === ""}>
          <InputLabel sx={{
            transform: "translate(0, 4px) scale(0.75)"
          }} variant="standard" htmlFor="uncontrolled-native-su">
            Strength Unit
          </InputLabel>
          <NativeSelect
            inputProps={{
              name: "strengthUnit",
              id: "uncontrolled-native-su",
            }}
            sx={{
              padding: "6px 0px",
              marginTop: "6px",
              marginBottom: "6px"
            }}
            value={props.entry.parameters.strengthUnit}
            onChange={(e) => {
              props.onEntryChange({
                ...props.entry,
                parameters: {
                  ...props.entry.parameters,
                  strengthUnit: e.target.value as UltimateStrengthUnit
                }
              })
            }}
            fullWidth
            disabled={props.disabled}
          >
            {props.entry.parameters.strengthUnit === "" &&
              <option value="" disabled>Select Unit</option>
            }
            <option value="mpa">MPa</option>
            <option value="psi">psi</option>
          </NativeSelect>
        </FormControl>
      </ListItem>
      <ListItem sx={{
        paddingLeft: "0px",
        paddingRight: "0px"
      }}>
        <NumericFormat
          key={`intercept-${props.maturityMethod.temperatureUnit}-${props.entry.parameters.strengthUnit}`}
          customInput={TextField}
          label={`Intercept (${strengthUnitDisplay})`}
          placeholder="Required"
          value={props.entry.parameters.intercept ?? ""}
          thousandSeparator=","
          decimalScale={props.entry.parameters.strengthUnit === "mpa" ? 2 : 0}
          fixedDecimalScale={props.entry.parameters.strengthUnit === "mpa"}
          allowNegative={true}
          onValueChange={(values) => {
            props.onEntryChange({
              ...props.entry,
              parameters: {
                ...props.entry.parameters,
                intercept: floatOrNull(values.value)
              }
            })
          }}
          fullWidth
          disabled={props.disabled}
          InputLabelProps={{
            shrink: true
          }}
          error={props.highlightRequiredFields && props.entry.parameters.intercept === null}
        />
      </ListItem>
      <ListItem sx={{
        paddingLeft: "0px",
        paddingRight: "0px"
      }}>
        <NumericFormat
          key={`slope-${props.entry.parameters.strengthUnit}`}
          customInput={TextField}
          placeholder="Required"
          label={`Slope (${strengthUnitDisplay}/º${props.maturityMethod.temperatureUnit.toUpperCase()}-hr)`}
          value={props.entry.parameters.slope ?? ""}
          thousandSeparator=","
          decimalScale={props.entry.parameters.strengthUnit === "mpa" ? 2 : 1}
          fixedDecimalScale={true}
          allowNegative={false}
          onValueChange={(values) => {
            props.onEntryChange({
              ...props.entry,
              parameters: {
                ...props.entry.parameters,
                slope: floatOrNull(values.value)
              }
            })
          }}
          fullWidth
          disabled={props.disabled}
          InputLabelProps={{
            shrink: true
          }}
          error={props.highlightRequiredFields && props.entry.parameters.slope === null}
        />
      </ListItem>
    </>
  )
}

export default LogarithmicDataView